import { MouseEvent, useEffect, useState } from 'react';

import HorizontalRule from '@@/components/HorizontalRule';
import LayoutSelfService from '@@/components/LayoutSelfService';
import { useRenttrack } from '@@/hooks/useRenttrack';

import analytics from '../../analytics';
import withGlobals, { TWithGlobals } from '../../withGlobals';
import { ErrorType } from '../ErrorHandler';

import imgApartment from './apartment@2x.png';
import imgDesk from './desk@2x.png';
import logos from './logos.svg';
import imgPhone from './phone@2x.png';

import './CreditLiftView.css';

function CreditLift({ onError }: Pick<TWithGlobals, 'onError'>) {
  const {
    hasLevelCreditAccount,
    createNewRenttrackUser,
    levelCreditRedirectUrl,
  } = useRenttrack();
  const [isInProgress, setIsInProgress] = useState(false);

  useEffect(() => {
    analytics.page();
  }, []);

  async function handleSignUp(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    setIsInProgress(true);

    if (!hasLevelCreditAccount) {
      try {
        await createNewRenttrackUser();
        analytics.track('LevelCredit User Provisioned', {
          noninteraction: 1,
        });
        window.location.replace(levelCreditRedirectUrl);
      } catch (error) {
        onError(error as ErrorType);
      }
    } else {
      window.location.replace(levelCreditRedirectUrl);
    }
  }

  return (
    <LayoutSelfService
      className="CreditLiftView"
      header="Building your credit is as easy as paying your rent"
      backgroundImgSrc={null}
      isLayoutContainer
    >
      <section className="CreditLiftView-section CreditLiftView-section--summary">
        <div className="CreditLiftView-section-container">
          <div className="CreditLiftView-section-wrapper">
            <img
              src={logos}
              alt="Credit Lift LevelCredit logo"
              className="CreditLiftView-logos"
            />
            <h2 className="CreditLiftView-section-heading">
              Get the credit you deserve for your on-time rent payments.
            </h2>
            <p className="CreditLiftView-section-info">
              Credit Lift, powered by LevelCredit, helps you build your credit
              and a brighter financial future by reporting your on-time rent
              payments to all three credit bureaus (Equifax, Experian and
              TransUnion).
            </p>
          </div>
        </div>
        <img
          src={imgApartment}
          alt="Apartment building"
          className="CreditLiftView-img"
        />
      </section>

      <section className="CreditLiftView-section CreditLiftView-section--howItWorks">
        <div className="CreditLiftView-section-container">
          <a
            href={levelCreditRedirectUrl}
            className="CreditLiftView-cta btn btn-secondary btn-assertive btn-block"
            onClick={(e) => handleSignUp(e)}
            data-testid="creditlift_signup_button_top"
          >
            {isInProgress ? (
              <i className="fas fa-spinner-third fa-spin" />
            ) : (
              'Sign me up for Credit Lift'
            )}
          </a>
          <div className="CreditLiftView-section-wrapper">
            <HorizontalRule />
            <h3 className="CreditLiftView-section-subheading">
              Here’s the lowdown
            </h3>
            <ul className="CreditLiftView-list">
              <li className="CreditLiftView-listItem">
                <i className="CreditLiftView-listItem-icon fas fa-check" />
                <span className="CreditLiftView-listItem-content">
                  <strong>Avoid the monthly hassle:</strong>
                  Schedule a payment online using your bank account and
                  LevelCredit will mail your rent check for you. Your check
                  arrives on time and the money stays in your bank account until
                  your landlord cashes it.
                </span>
              </li>

              <li className="CreditLiftView-listItem">
                <i className="CreditLiftView-listItem-icon fas fa-check" />
                <span className="CreditLiftView-listItem-content">
                  <strong>Works with almost any landlord:</strong>
                  As long as your landlord will accept a rent check in the mail,
                  you&apos;re good to go. Your landlord doesn&apos;t have to
                  verify your payments or sign up for anything.
                </span>
              </li>

              <li className="CreditLiftView-listItem">
                <i className="CreditLiftView-listItem-icon fas fa-check" />
                <span className="CreditLiftView-listItem-content">
                  <strong>Automatically build your credit score:</strong>
                  LevelCredit will notify all three credit bureaus every time
                  your landlord deposits your rent check, so it shows up on your
                  credit report and helps build your score.
                </span>
              </li>
            </ul>
          </div>
        </div>
        <img src={imgDesk} alt="Computer desk" className="CreditLiftView-img" />
      </section>

      <section className="CreditLiftView-section CreditLiftView-section--benefits">
        <div className="CreditLiftView-section-container">
          <h2 className="CreditLiftView-section-heading">
            Real benefits, with real results.
          </h2>
          <p className="CreditLiftView-section-info">
            Reporting a year&apos;s worth of rent payments, can help lift your
            credit score an average of:
          </p>
          <img
            src={imgPhone}
            alt="Phone with message illustration"
            className="CreditLiftView-img"
          />
          <p className="CreditLiftView-section-info">
            In general, the lower your starting score, the bigger the
            improvement. You&apos;re already paying rent - start getting credit
            for it.
          </p>
        </div>
      </section>

      <section className="CreditLiftView-section CreditLiftView-section--cta">
        <div className="CreditLiftView-section-container">
          <h2 className="CreditLiftView-section-heading">
            Get credit for paying your rent.
          </h2>
          <a
            href={levelCreditRedirectUrl}
            className="CreditLiftView-cta btn btn-secondary btn-assertive btn-block"
            onClick={handleSignUp}
            data-testid="creditlift_signup_button_bottom"
          >
            {isInProgress ? (
              <i className="fas fa-spinner-third fa-spin" />
            ) : (
              'Sign me up for Credit Lift'
            )}
          </a>
          <p className="CreditLiftView-section-info">
            <small>
              Credit score is one part of your financial life and can&apos;t
              alone predict a bright financial future. We can&apos;t and
              don&apos;t offer financial advice, so if you have questions, you
              should visit a certified financial professional.
            </small>
          </p>
        </div>
      </section>
    </LayoutSelfService>
  );
}

export default withGlobals(CreditLift);
